import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import { getApiEndpoints } from '@/services/api/endpoints';
import { normalizeDeep } from '@/lib/dataSanity';
import type { CreateItemResponse } from '@/services/api/types';
import type { PropertyData } from '@/domain/entities/property-object';
import type { BatchCreatePropertyResponse } from './propertyDraftTypes';

export const batchCreatePropertyObject = createAppAsyncThunk<
  BatchCreatePropertyResponse,
  { property: PropertyData; initialOwnerUserID: string }
>(
  'propertyDraft/batchCreatePropertyObject',
  async ({ property, initialOwnerUserID }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;
    const ownerUserID = initialOwnerUserID.trim();

    if (!ownerUserID) {
      return rejectWithValue({ message: 'Initial owner user ID is required' });
    }

    try {
      const payload = {
        op: 'propertyBatchCreate',
        dryRun: false, // you control this
        initialOwnerUserID: ownerUserID,
        templateID: property.templateID,
        property: property.property,
        locations: property.locations ?? [],
        systems: property.systems ?? [],
        utilities: property.utilities ?? [],
      };

      const endpoint = getApiEndpoints().core.adminCorePaths.batchCreateProperty;
      const response = await apiService.post<
        CreateItemResponse<BatchCreatePropertyResponse> | BatchCreatePropertyResponse
      >(endpoint, normalizeDeep(payload));
      const result = 'data' in response ? response.data : response;

      if (!result?.ok) {
        const msg = formatBatchCreateError(result, 'data' in response ? response.message : undefined);
        return rejectWithValue({ message: msg });
      }

      return result;
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);

function formatBatchCreateError(res: BatchCreatePropertyResponse | null | undefined, envelopeMessage?: string): string {
  const parts: string[] = [];

  if (res?.message && res.message !== 'OK') parts.push(res.message);
  if (Array.isArray(res?.logs)) {
    for (const log of res.logs) {
      if (typeof log === 'string' && log.trim()) parts.push(log);
      else if (typeof log !== 'string') {
        if (log.error?.trim()) parts.push(log.error);
        else if (log.message?.trim()) parts.push(log.message);
      }
    }
  }
  if (envelopeMessage && envelopeMessage !== 'OK') parts.push(envelopeMessage);
  if (parts.length === 0) return 'Batch create failed';
  return Array.from(new Set(parts)).join('\n');
}
